/**
 * How an alert looks and where it leads: an icon per type, a badge tone per
 * severity, and the page of the record it points at (null when it points at
 * nothing the app can open yet).
 */

import type { LucideIcon } from 'lucide-react';
import {
  ArrowRightLeft,
  Banknote,
  BellOff,
  ClipboardCheck,
  Copy,
  FileWarning,
  FileX,
  Landmark,
  MailWarning,
  MailX,
  ShieldAlert,
  ShieldX,
} from 'lucide-react';
import type { AlertEntityRef, AlertSeverity, AlertType } from '@nord-living/shared';

import type { BadgeTone } from '@/components/ui';

export const ALERT_ICONS: Record<AlertType, LucideIcon> = {
  send_failed: MailX,
  webhook_rejected: ShieldAlert,
  reminder_failed: BellOff,
  extraction_failed: FileWarning,
  duplicate_document: Copy,
  payment_unapplied: Banknote,
  bank_unmatched: Landmark,
  bank_import_failed: FileX,
  transfer_reported: ArrowRightLeft,
  approval_decided: ClipboardCheck,
  digest_failed: MailWarning,
  two_factor_failures: ShieldX,
};

export const SEVERITY_TONES: Record<AlertSeverity, BadgeTone> = {
  info: 'info',
  warning: 'warning',
  critical: 'danger',
};

/** Open warning + critical alerts — what the header bell counts. */
export const URGENT_SEVERITIES: AlertSeverity[] = ['warning', 'critical'];

const HREFS: Partial<Record<AlertEntityRef['type'], (id: string) => string>> = {
  invoice: (id) => `/invoices/${id}`,
  payment: (id) => `/payments/${id}`,
  supplier_document: (id) => `/purchases/receipts/${id}`,
  bank_account: (id) => `/banking?account=${id}`,
};

export function alertHref(entity: AlertEntityRef | null): string | null {
  if (!entity) return null;
  const href = HREFS[entity.type];
  return href ? href(encodeURIComponent(entity.id)) : null;
}
